import { computeRemainingQuota } from '@/lib/billing/computeRemainingQuota';
import { isPaidSubscription, type SubscriptionLike } from '@/lib/quota/isPaidSubscription';

/**
 * How close a free user is to running out of the quota that matters to them.
 *
 * Every quota surface in the app used to be binary: it spoke only once the
 * limit was already gone. The moment *before* exhaustion is the higher-intent
 * one — the user is still working rather than blocked — so this derives an
 * "approaching" level as well, from data `myData` already carries.
 */

/** `Sidebar` renders any cap at or above this as "Unlimited". */
export const UNLIMITED_QUOTA_SENTINEL = 1_000_000;

/** Share of a time quota that must be consumed before warning. */
export const TIME_APPROACHING_RATIO = 0.8;

export type QuotaMetric =
  | 'clips'
  | 'processing_time'
  | 'library_curations'
  | 'library_exports'
  | 'library_video_time';

export type QuotaPressureLevel = 'approaching' | 'exhausted';

export interface QuotaPressure {
  metric: QuotaMetric;
  level: QuotaPressureLevel;
  used: number;
  max: number;
  remaining: number;
  /** Floored, so the number shown can never claim a threshold that was not met. */
  percentage: number;
}

export interface QuotaPressureSubscriptionLike extends SubscriptionLike {
  current_month_clips?: number | null;
  current_month_processing_seconds?: number | null;
  current_month_library_curations?: number | null;
  current_month_library_exports?: number | null;
  current_month_library_video_seconds?: number | null;
  plan?:
    | (SubscriptionLike['plan'] & {
        max_clips?: number | null;
        processing_seconds?: number | null;
        max_library_curations?: number | null;
        max_library_exports?: number | null;
        max_library_video_seconds?: number | null;
      })
    | null;
}

export type QuotaMetricKind = 'countable' | 'time';

/**
 * The one threshold rule, shared by every surface that tones a usage bar.
 * Countable quotas warn at a single unit left; time quotas warn at a ratio,
 * because a count like the free tier's 3 curations never reaches 80% before
 * it is simply gone.
 */
export function classifyQuotaLevel({
  kind,
  used,
  max,
}: {
  kind: QuotaMetricKind;
  used: number;
  max: number;
}): QuotaPressureLevel | null {
  if (max <= 0 || max >= UNLIMITED_QUOTA_SENTINEL) return null;

  const remaining = computeRemainingQuota(max, used);
  if (remaining <= 0) return 'exhausted';

  const approaching =
    kind === 'countable' ? remaining === 1 : used / max >= TIME_APPROACHING_RATIO;

  return approaching ? 'approaching' : null;
}

type MetricKind = QuotaMetricKind;

interface MetricSpec {
  metric: QuotaMetric;
  kind: MetricKind;
  used: (s: QuotaPressureSubscriptionLike) => number;
  max: (s: QuotaPressureSubscriptionLike) => number;
}

const METRICS: Record<QuotaMetric, MetricSpec> = {
  clips: {
    metric: 'clips',
    kind: 'countable',
    used: (s) => s.current_month_clips || 0,
    max: (s) => s.plan?.max_clips || 0,
  },
  processing_time: {
    metric: 'processing_time',
    kind: 'time',
    used: (s) => s.current_month_processing_seconds || 0,
    max: (s) => s.plan?.processing_seconds || 0,
  },
  library_curations: {
    metric: 'library_curations',
    kind: 'countable',
    used: (s) => s.current_month_library_curations || 0,
    max: (s) => s.plan?.max_library_curations || 0,
  },
  library_exports: {
    metric: 'library_exports',
    kind: 'countable',
    used: (s) => s.current_month_library_exports || 0,
    max: (s) => s.plan?.max_library_exports || 0,
  },
  library_video_time: {
    metric: 'library_video_time',
    kind: 'time',
    used: (s) => s.current_month_library_video_seconds || 0,
    max: (s) => s.plan?.max_library_video_seconds || 0,
  },
};

/**
 * Which quotas each persona actually sees, mirroring the `Sidebar` branch on
 * `onboarding_objective`. Warning someone about a limit their UI never shows
 * them is noise they cannot act on.
 */
const OWN_BRAND_METRICS: QuotaMetric[] = ['processing_time', 'clips'];
const OTHER_CHANNELS_METRICS: QuotaMetric[] = [
  'library_curations',
  'library_exports',
  'library_video_time',
];

function evaluate(
  spec: MetricSpec,
  subscription: QuotaPressureSubscriptionLike,
): QuotaPressure | null {
  const max = spec.max(subscription);
  const used = spec.used(subscription);

  const level = classifyQuotaLevel({ kind: spec.kind, used, max });
  if (!level) return null;

  return {
    metric: spec.metric,
    level,
    used,
    max,
    remaining: computeRemainingQuota(max, used),
    percentage: Math.min(100, Math.floor((used / max) * 100)),
  };
}

/**
 * The single most pressured quota for this subscriber, or `null` when there is
 * nothing worth saying. Paid subscribers always get `null`.
 */
export function deriveQuotaPressure(
  subscription: QuotaPressureSubscriptionLike | null | undefined,
  objective: string | null | undefined,
): QuotaPressure | null {
  if (!subscription) return null;
  if (isPaidSubscription(subscription)) return null;

  const metrics =
    objective === 'other_channels' ? OTHER_CHANNELS_METRICS : OWN_BRAND_METRICS;

  const pressures = metrics
    .map((metric) => evaluate(METRICS[metric], subscription))
    .filter((pressure): pressure is QuotaPressure => pressure !== null);

  if (pressures.length === 0) return null;

  // Exhausted outranks approaching; within a level, the tightest quota wins.
  return pressures.reduce((worst, candidate) => {
    if (candidate.level !== worst.level) {
      return candidate.level === 'exhausted' ? candidate : worst;
    }
    return candidate.percentage > worst.percentage ? candidate : worst;
  });
}
